// ProjectsPageV3
// Graphics Admin soft-archive action. No permanent project delete.
// ProjectsPageV2
// Status filter aligned with StatusBadgeV5.
import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { StatusBadge, PriorityBadge } from "../components/StatusBadge";
import { FolderKanban, Plus, Search, Eye, Archive, Sparkles, CheckCircle } from "lucide-react";
import { ProjectModal } from "../components/ProjectModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "graphics", label: "Graphics Projects", source: "graphics" },
  { id: "opportunities", label: "Sales Opportunities", source: "spiro" },
  { id: "email", label: "Email Intake", source: "email" },
];

const cleanIdentity = (value) => String(value || "").trim().toLowerCase();

const userIdentityValues = (user) => {
  const identities = new Set([
    cleanIdentity(user?.id),
    cleanIdentity(user?.full_name),
    cleanIdentity(user?.display_name),
    cleanIdentity(user?.username),
    cleanIdentity(user?.email),
  ].filter(Boolean));

  [user?.username, user?.email].forEach((value) => {
    const normalized = cleanIdentity(value);
    if (normalized.includes("@")) {
      identities.add(normalized.split("@", 1)[0]);
    }
  });

  return identities;
};

const projectAssignedToUser = (project, user) => {
  if (!project || !user) return false;

  const userId = String(user.id || "").trim();

  if (
    userId
    && (
      String(project.created_by_user_id || "").trim() === userId
      || String(project.assigned_user_id || "").trim() === userId
    )
  ) {
    return true;
  }

  const identities = userIdentityValues(user);

  const valueMatches = (value) => {
    if (Array.isArray(value)) {
      return value.some((item) => identities.has(cleanIdentity(item)));
    }

    const normalized = cleanIdentity(value);
    return Boolean(normalized && identities.has(normalized));
  };

  return [
    "isr",
    "osr",
    "assigned_isr",
    "assigned_osr",
    "assigned_to",
    "assigned_user",
    "created_by",
    "created_by_email",
    "submitted_by",
    "submitted_by_email",
  ].some((field) => valueMatches(project[field]));
};

export const ProjectsPageV3 = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isManager, canCreateProject, canViewAllProjects, user } = useAuth();

  const requestedTab = searchParams.get("tab") || "graphics";
  const initialTab = isAdmin && TABS.some((tab) => tab.id === requestedTab) ? requestedTab : "graphics";

  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [customerFilter, setCustomerFilter] = useState(searchParams.get("customer_id") || "");
  const [manufacturerFilter, setManufacturerFilter] = useState(searchParams.get("manufacturer_id") || "");
  const [showModal, setShowModal] = useState(false);
  const [aiMatching, setAiMatching] = useState(false);

  const defaultViewScope = isAdmin || isManager ? "all" : "mine";
  const [viewScope, setViewScope] = useState(() => {
    const requestedView = searchParams.get("view");

    if (requestedView === "all" && canViewAllProjects) {
      return "all";
    }

    if (requestedView === "mine") {
      return "mine";
    }

    return defaultViewScope;
  });

  const pageTitle = "Projects";

  const pageDescription = canViewAllProjects && viewScope === "all"
    ? "All artwork projects available to you."
    : "Artwork projects assigned to or created by you.";

  const searchPlaceholder = canViewAllProjects && viewScope === "all"
    ? "Search all projects..."
    : "Search my projects...";

  const fetchData = async () => {
    try {
      const effectiveTab = isAdmin ? activeTab : "graphics";
      const tab = TABS.find((item) => item.id === effectiveTab) || TABS[0];
      const params = new URLSearchParams();
      params.append("source", tab.source);
      if (statusFilter) params.append("status", statusFilter);
      if (customerFilter) params.append("customer_id", customerFilter);
      if (manufacturerFilter) params.append("manufacturer_id", manufacturerFilter);

      const [projectsRes, customersRes, manufacturersRes] = await Promise.all([
        axios.get(`${API}/projects?${params}`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/manufacturers`),
      ]);
      setProjects((projectsRes.data || []).filter((item) => item.is_archived !== true));
      setCustomers(customersRes.data);
      setManufacturers(manufacturersRes.data);
    } catch (err) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const effectiveTab = isAdmin ? activeTab : "graphics";
    const params = new URLSearchParams();

    params.set(
      "view",
      canViewAllProjects && viewScope === "all" ? "all" : "mine",
    );
    if (isAdmin && effectiveTab !== "graphics") params.set("tab", effectiveTab);
    if (statusFilter) params.set("status", statusFilter);
    if (customerFilter) params.set("customer_id", customerFilter);
    if (manufacturerFilter) params.set("manufacturer_id", manufacturerFilter);
    setSearchParams(params, { replace: true });

    if (!isAdmin && activeTab !== "graphics") setActiveTab("graphics");
    setLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter, customerFilter, manufacturerFilter, isAdmin, canViewAllProjects, viewScope]);

  useEffect(() => {
    const urlView = searchParams.get("view");
    const urlTab = searchParams.get("tab");

    if (urlView === "all" && canViewAllProjects) {
      setViewScope("all");
    } else if (urlView === "mine") {
      setViewScope("mine");
    }
    const urlStatus = searchParams.get("status");
    const urlCustomer = searchParams.get("customer_id");
    const urlManufacturer = searchParams.get("manufacturer_id");
    if (isAdmin && urlTab && TABS.some((tab) => tab.id === urlTab)) setActiveTab(urlTab);
    if (urlStatus) setStatusFilter(urlStatus);
    if (urlCustomer) setCustomerFilter(urlCustomer);
    if (urlManufacturer) setManufacturerFilter(urlManufacturer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopedProjects = useMemo(() => {
    if (!canViewAllProjects || viewScope === "all") {
      return projects;
    }

    return projects.filter(
      (project) => projectAssignedToUser(project, user),
    );
  }, [canViewAllProjects, projects, user, viewScope]);

  const filteredProjects = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return scopedProjects;
    }

    return scopedProjects.filter((project) => (
      String(project.name || "").toLowerCase().includes(term)
      || String(project.customer_name || "").toLowerCase().includes(term)
      || String(project.manufacturer_name || "").toLowerCase().includes(term)
    ));
  }, [scopedProjects, search]);

  const handleArchiveProject = async (id) => {
    if (!window.confirm("Archive this project? It will leave active views, but all files and history will be retained.")) return;
    try {
      await axios.post(`${API}/project-archive/${id}/archive`);
      toast.success("Project archived");
      fetchData();
    } catch (err) {
      toast.error("Failed to archive project");
    }
  };

  const handleAiMatch = async () => {
    setAiMatching(true);
    try {
      const response = await axios.post(`${API}/spiro/ai-match-customers`);
      const data = response.data;
      if (data.matched > 0) {
        toast.success(`AI matched ${data.matched} of ${data.total_unknown} projects to customers`);
        fetchData();
      } else {
        toast.info("AI couldn't find matches for the remaining projects");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI matching failed");
    } finally {
      setAiMatching(false);
    }
  };

  const handleAcceptProject = async (projectId, projectName) => {
    if (!window.confirm(`Accept "${projectName}" into Graphics Projects?`)) return;
    try {
      await axios.post(`${API}/projects/${projectId}/accept`);
      toast.success(`"${projectName}" moved to Graphics Projects`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept project");
    }
  };

  const unknownCount = activeTab === "opportunities"
    ? projects.filter((project) => project.customer_name === "Unknown (Spiro)").length
    : 0;

  const tabLabel = TABS.find((tab) => tab.id === activeTab)?.label || "Projects";

  const emptyMessage = activeTab === "graphics"
    ? canViewAllProjects && viewScope === "all"
      ? "No projects match the current filters."
      : "No projects are currently assigned to or created by you."
    : activeTab === "opportunities"
      ? "Sync opportunities from Spiro to see them here."
      : "Poll the email inbox to see projects here.";

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{pageTitle}</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin && activeTab !== "graphics" ? tabLabel : pageDescription}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === "opportunities" && unknownCount > 0 && isAdmin && (
            <button
              data-testid="ai-match-btn"
              onClick={handleAiMatch}
              disabled={aiMatching}
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              <Sparkles size={18} className={aiMatching ? "animate-pulse" : ""} />
              {aiMatching ? "Matching..." : `Match Customers from Spiro (${unknownCount})`}
            </button>
          )}
          {canViewAllProjects && activeTab === "graphics" && (
            <div className="inline-flex rounded-lg border border-border bg-card p-1">
              <button
                type="button"
                onClick={() => setViewScope("mine")}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  viewScope === "mine"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                My Projects
              </button>
              <button
                type="button"
                onClick={() => setViewScope("all")}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  viewScope === "all"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                All Projects
              </button>
            </div>
          )}
          {canCreateProject && activeTab === "graphics" && (
            <button
              data-testid="create-project-btn"
              onClick={() => navigate("/intake")}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus size={20} />
              Start Project
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="flex gap-1 mb-6 bg-secondary/50 p-1 rounded-lg w-fit" data-testid="project-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => {
                setActiveTab(tab.id);
                setStatusFilter("");
                setCustomerFilter("");
                setManufacturerFilter("");
                setSearch("");
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="search-projects"
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <select
          data-testid="filter-status"
          aria-label="Filter projects by current workflow status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="px-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">All Statuses</option>
          <option value="ready_for_submission">Ready for Submission</option>
          <option value="artwork_submitted">Artwork Submitted</option>
          <option value="pdf_proof">PDF Proof</option>
          <option value="swatch">Swatch</option>
          <option value="pilot">Pilot</option>
          <option value="graphics_complete">Graphics Complete</option>
          <option value="new_project">New Project</option>
          <option value="graphic_team_review">Graphic Team Review</option>
          <option value="pdf_proof_phase">PDF Proof Phase</option>
          <option value="swatch_drawdown_phase">Swatch Drawdown</option>
          <option value="pilot_proof_phase">Pilot Proof Phase</option>
        </select>

        <select
          data-testid="filter-customer"
          value={customerFilter}
          onChange={(event) => setCustomerFilter(event.target.value)}
          className="px-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">All Customers</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>

        <select
          data-testid="filter-manufacturer"
          value={manufacturerFilter}
          onChange={(event) => setManufacturerFilter(event.target.value)}
          className="px-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">All Manufacturers</option>
          {manufacturers.map((manufacturer) => (
            <option key={manufacturer.id} value={manufacturer.id}>
              {manufacturer.name}
            </option>
          ))}
        </select>

        {(statusFilter || customerFilter || manufacturerFilter) && (
          <button
            onClick={() => {
              setStatusFilter("");
              setCustomerFilter("");
              setManufacturerFilter("");
            }}
            className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-secondary transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground animate-pulse">Loading...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="empty-state">
            <FolderKanban size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">No projects found</p>
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Customer</th>
                <th>Manufacturer</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Version</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => (
                <tr key={project.id} data-testid={`project-row-${project.id}`}>
                  <td>
                    <Link to={`/projects/${project.id}`} className="font-medium hover:underline">
                      {project.name}
                    </Link>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-muted-foreground capitalize">{project.project_type}</span>
                      {project.source === "spiro" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-950/50 text-blue-300 rounded border border-blue-800/50">Spiro</span>
                      )}
                      {project.source === "email" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-purple-950/50 text-purple-300 rounded border border-purple-800/50">Email</span>
                      )}
                      {project.ai_parsed && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-950/50 text-emerald-300 rounded border border-emerald-800/50">AI</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={project.customer_name === "Unknown (Spiro)" ? "text-yellow-400" : "text-muted-foreground"}>
                      {project.customer_name || "—"}
                    </span>
                    {project.ai_customer_match && (
                      <span className="ml-1 text-[10px] px-1 py-0.5 bg-purple-950/50 text-purple-300 rounded">
                        AI {project.ai_customer_match.confidence}
                      </span>
                    )}
                  </td>
                  <td className="text-muted-foreground">{project.manufacturer_name || "—"}</td>
                  <td><StatusBadge status={project.status} /></td>
                  <td><PriorityBadge priority={project.priority} /></td>
                  <td className="mono text-sm">v{project.current_version}</td>
                  <td className="text-sm text-muted-foreground">
                    {new Date(project.updated_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {activeTab === "email" && isAdmin && (
                        <button
                          data-testid={`accept-project-${project.id}`}
                          onClick={() => handleAcceptProject(project.id, project.name)}
                          className="p-2 hover:bg-green-950/30 text-green-400 rounded-lg transition-colors"
                          title="Accept into Graphics Projects"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      <Link
                        to={`/projects/${project.id}`}
                        data-testid={`view-project-${project.id}`}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      >
                        <Eye size={16} />
                      </Link>
                      {isAdmin && (
                        <button
                          data-testid={`delete-project-${project.id}`}
                          data-project-archive-role="graphics_admin"
                          onClick={() => handleArchiveProject(project.id)}
                          className="p-2 hover:bg-red-950/30 text-red-400 rounded-lg transition-colors"
                        >
                          <Archive size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ProjectModal
          customers={customers}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};
